import { useState } from 'react'
import { css, cx } from '@fable/theme'

import Loader, { LoaderType } from '../../../../../components/loader'
import ShareButton from '../../../../../components/share_button/ShareButton'

import { BookList } from '../../../../../types'
import { Color } from '@fable/legacy'
import { BREAKPOINTS, CATEGORIES } from '../../../../../constants'
import { Button, FlexBox, Text } from '@fable/components'
import { useSidebar } from '../../../../../hooks'
import { BOOK_LIST_EDIT } from '../../../constants'

interface Props {
  info: BookList
}

export const BookListMasthead: React.VFC<Props> = ({ info }) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const { onOpenSidebar } = useSidebar()
  const styles = getStyles()
  const {
    name,
    cover_image,
    description,
    user,
    bookCount,
    color,
    isListOwner,
    url,
  } = info

  const onImageLoaded = () => {
    setIsImageLoaded(true)
  }

  const renderLoader = !isImageLoaded && (
    <Loader isLight={true} type={LoaderType.contained} />
  )

  const renderCurator = () => {
    let label
    if (bookCount === 1) {
      label = 'book created by'
    } else {
      label = 'books created by'
    }
    const curatorLabel = `${bookCount} ${label}`

    return (
      <div className={cx(styles.curator)}>
        <div className={cx(styles.curatorImage)}>
          <img src={user.pic_resize} alt={`${user.display_name} Avatar`} />
        </div>
        <Text className={styles.curatorInfo} type="label" sizing="L">
          {curatorLabel}
        </Text>
        <Text className={styles.curatorInfo} type="label" sizing="L">
          {user.display_name}
        </Text>
      </div>
    )
  }

  return (
    <section>
      <div className="content">
        <div
          className={cx(styles.imageWrap)}
          style={{ backgroundColor: isImageLoaded ? color : Color.whiteSmoke }}
        >
          {renderLoader}
          <img
            className={cx(
              styles.image,
              isImageLoaded && styles.imageLoaded,
              'image'
            )}
            src={cover_image}
            alt={name}
            onLoad={onImageLoaded}
          />
        </div>
        <Text
          className={cx(styles.title)}
          type="header"
          sizing={{ base: 'M', mobileL: 'L' }}
        >
          {name}
        </Text>
        <p className={cx(styles.description)}>{description}</p>
        {renderCurator()}
      </div>
      <FlexBox justifyContent="center">
        <ShareButton
          className={cx(isListOwner && styles.button)}
          category={CATEGORIES.bookList}
          title={isListOwner ? 'Share' : null}
          url={url}
          translucent
        />
        {isListOwner && (
          <Button
            className={styles.button}
            icon="edit"
            label="Edit"
            onClick={() => onOpenSidebar(BOOK_LIST_EDIT)}
            translucent
            dataTestId="listEdit"
          />
        )}
      </FlexBox>
    </section>
  )
}

const getStyles = () => ({
  imageWrap: css`
    padding: 12px;
    width: 154px;
    height: 154px;
    border-radius: 8px;
    position: relative;
    transition: background-color ease 0.2s;

    @media only screen and (min-width: ${BREAKPOINTS.mobile}) {
      padding: 16px;
      width: 244px;
      height: 244px;
    }
  `,
  image: css`
    width: 100%;
    height: 100%;
    border-radius: 4px;
    object-fit: cover;
    box-shadow: 0px 12px 12px rgba(0, 0, 0, 0.12);
    opacity: 0;
    transition: opacity ease 0.2s;
  `,
  imageLoaded: css`
    opacity: 1;
  `,
  title: css`
    margin: 20px 0 12px !important;
    font-size: 28px !important;
    line-height: 28px !important;
    text-align: center;
    color: ${Color.whiteFang};

    @media only screen and (min-width: ${BREAKPOINTS.mobile}) {
      margin: 28px 0 !important;
    }

    @media only screen and (min-width: ${BREAKPOINTS.mobile}) and (max-width: ${BREAKPOINTS.tablet}) {
      font-size: 40px !important;
      line-height: 40px !important;
    }

    @media only screen and (min-width: ${BREAKPOINTS.tablet}) {
      font-size: 44px !important;
      line-height: 46px !important;
    }
  `,
  description: css`
    margin: 0 auto;
    max-width: 280px;
    font-size: 14px !important;
    line-height: 20px !important;
    letter-spacing: 0.02em !important;
    text-align: center;
    color: ${Color.whiteFang};

    @media only screen and (min-width: ${BREAKPOINTS.mobile}) {
      max-width: 286px;
      font-size: 15px !important;
    }
  `,
  curator: css`
    margin: 28px 0 32px;
    display: flex;
    align-items: center;
    flex-direction: column;
  `,
  curatorImage: css`
    margin-bottom: 8px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
    position: relative;
    overflow: hidden;

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    :after {
      content: '';
      width: calc(100% - 4px);
      height: calc(100% - 4px);
      border-radius: 50%;
      position: absolute;
      top: 50%;
      left: 50%;
      border: 2px solid rgba(255, 255, 255, 0.2);
      transform: translate(-50%, -50%);
    }
  `,
  curatorInfo: css`
    font-size: 12px !important;
    line-height: 18px !important;
    letter-spacing: 0.02em !important;
    text-align: center;
    color: ${Color.whiteFang};
    opacity: 0.8;
    text-transform: uppercase;
  `,
  button: css`
    max-width: none;
    width: 130px;
    height: 44px;

    :not(:last-child) {
      margin-right: 8px;
    }
  `,
})
